import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { HttpService } from 'src/app/Core/interceptors/http/http.service';
import { environment } from 'src/environments/environment';
import {
  GroupedNotifications,
  INotification,
  IRawNotification,
  NotificationType,
  NotificationTypesMap,
  NotificationsApiResponse,
  PusherNotificationPayload,
} from '../../../../../../Shared/_models/notification.interace';
import { LocalDateTimePipe } from 'src/app/Shared/_pipes/local-date-time';
import { TimeZoneService } from 'src/app/Shared/services/time-zone/time-zone.service';
import { NotificationRenderService } from 'src/app/Shared/services/notification-render/notification-render.service';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';
import { AnalyticsService } from 'src/app/Shared/_services/analytics/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private _notification = new BehaviorSubject<any>(null);
  notification$ = this._notification.asObservable();

  private _notifications = new BehaviorSubject<INotification[]>([]);
  notifications$ = this._notifications.asObservable();

  /** Store notification types for rendering */
  private _notificationTypes = new BehaviorSubject<NotificationTypesMap>({});
  notificationTypes$ = this._notificationTypes.asObservable();

  constructor(
    private http: HttpService,
    private localDateTime: LocalDateTimePipe,
    private timezoneService: TimeZoneService,
    private renderService: NotificationRenderService,
    private localeService: LocaleService,
    private analyticsService: AnalyticsService
  ) {
    // Re-render notifications when language changes
    this.localeService.languageCode$.subscribe((langCode) => {
      this.reRenderNotificationsOnLanguageChange(langCode);
    });
  }

  /**
   * Store notification types from API response
   * @param types Notification types map or array
   */
  setNotificationTypes(types: NotificationTypesMap | NotificationType[]): void {
    const currentTypes = this._notificationTypes.value;
    let newTypes: NotificationTypesMap;

    if (Array.isArray(types)) {
      newTypes = types.reduce((acc, type) => {
        acc[type.id] = type;
        return acc;
      }, {} as NotificationTypesMap);
    } else {
      newTypes = types;
    }

    // Merge with existing types
    this._notificationTypes.next({ ...currentTypes, ...newTypes });
  }

  /**
   * Get notification type by ID
   */
  getNotificationType(typeId: number): NotificationType | undefined {
    return this._notificationTypes.value[typeId];
  }

  /**
   * Get all notification types
   */
  getNotificationTypes(): NotificationTypesMap {
    return this._notificationTypes.value;
  }

  /**
   * Process raw notifications from API and render title/body
   */
  processRawNotifications(rawNotifications: IRawNotification[]): INotification[] {
    return this.renderService.processNotifications(
      rawNotifications,
      this._notificationTypes.value
    );
  }

  /**
   * Process a single Pusher notification
   */
  processPusherNotification(payload: PusherNotificationPayload): INotification {
    // Store the notification type
    this.setNotificationTypes([payload.notification_type]);

    // Process and return the notification
    return this.renderService.processNotification(
      payload.notification,
      payload.notification_type
    );
  }

  /**
   * Re-render all stored notifications when language changes
   */
  private reRenderNotificationsOnLanguageChange(langCode: string): void {
    const currentNotifications = this._notifications.value;
    if (currentNotifications.length > 0) {
      const reRendered = this.renderService.reRenderNotifications(
        currentNotifications,
        this._notificationTypes.value,
        langCode
      );
      this._notifications.next(reRendered);
    }
  }

  setRecievedNotification(not: any) {
    this._notification.next(not);
  }
  getNotifications(page: number, size: number): Observable<any> {
    return this.http.getWithParams(`${environment.url}/notifications`, {
      page_size: size,
      page_number: page,
    });
  }

  markNotficationAsRead(id: number, notificationType?: string) {
    return this.http.post(`${environment.url}/markNotificationAsReaded`, {
      notification_id: id,
    }).pipe(
      tap(() => {
        // this.analyticsService.trackNotificationRead(id.toString(), notificationType);
      })
    );
  }
  markAllNotificationsAsRead(id: number) {
    const unreadCount = this.countUnreadNotifications(this._notifications.value);
    return this.http.post(`${environment.url}/markAllNotificationAsReaded`, {
      notification_ids: [id],
    }).pipe(
      tap(() => {
        // this.analyticsService.trackAllNotificationsRead(unreadCount);
      })
    );
  }
  updateNotificationReadStatus(notificationId: number, read: boolean): void {
    // Get current notifications array
    const currentNotifications = this._notifications.value;

    // Check if we have notifications to update
    if (!currentNotifications || !Array.isArray(currentNotifications)) {
      console.warn('No notifications array found to update');
      return;
    }

    // Update the specific notification
    const updatedNotifications = currentNotifications.map(
      (notification: INotification) =>
        notification.id === notificationId
          ? { ...notification, read_at: read ? new Date().toISOString() : null }
          : notification
    );

    // Update the notifications array
    this._notifications.next(updatedNotifications);
  }

  groupNotificationsByDate(
    notifications: INotification[]
  ): GroupedNotifications[] {
    const groupedMap = new Map<string, INotification[]>();
    const now = new Date();

    notifications.forEach((notification) => {
      // Use your pipe to convert UTC to local time, then extract date
      const localDateTime = this.localDateTime.convertFromISOFormat(
        notification.created_at
      );
      const dateKey = localDateTime.split(' ')[0]; // Extract "2025-06-11" from "2025-06-11 04:25:04"

      if (!groupedMap.has(dateKey)) {
        groupedMap.set(dateKey, []);
      }
      groupedMap.get(dateKey)?.push(notification);
    });

    return Array.from(groupedMap.entries())
      .map(([dateKey, notifications]) => {
        // Find the most recent notification in this group
        const mostRecentNotification = notifications.reduce(
          (latest, current) => {
            return new Date(current.created_at) > new Date(latest.created_at)
              ? current
              : latest;
          }
        );

        // Calculate time difference using the most recent notification
        const diffInMs =
          now.getTime() - new Date(mostRecentNotification.created_at).getTime();

        const dateInfo = this.getDateInfo(dateKey);
        return {
          date: dateKey,
          displayDate: dateInfo.displayDate,
          dateType: dateInfo.dateType, // 'today', 'yesterday', or 'other'
          elapsedSeconds: Math.floor(diffInMs / 1000),
          timeAgo: this.getTimeAgoFromMs(diffInMs),
          notifications: notifications.sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          ),
          unreadCount: this.countUnreadNotifications(notifications),
        };
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }
  private getTimeAgoFromMs(diffInMs: number): string {
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    } else {
      return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
    }
  }
  /**
   * Get date info including type and formatted display string
   * @param dateString - Date string in YYYY-MM-DD format
   * @returns Object with dateType ('today', 'yesterday', 'other') and displayDate
   */
  private getDateInfo(dateString: string): { dateType: 'today' | 'yesterday' | 'other'; displayDate: string } {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Format dates for comparison (YYYY-MM-DD)
    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (dateString === todayStr) {
      return { dateType: 'today', displayDate: '' };
    } else if (dateString === yesterdayStr) {
      return { dateType: 'yesterday', displayDate: '' };
    } else {
      // Format as "Month DD, YYYY" using current locale
      const locale = this.localeService.locale || 'en-US';
      const displayDate = this.timezoneService.formatDateToLocale(dateString, locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return { dateType: 'other', displayDate };
    }
  }

  /**
   * Convert elapsed seconds to human-readable time ago
   * @param seconds - Elapsed seconds
   */
  private getTimeAgoFromSeconds(diffInMs: number): string {
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    } else {
      return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
    }
  }
  /**
   * Get notification age in a human readable format
   * @param notification - Notification object
   */
  getNotificationAge(notificationCreated: any): string {
    const now = new Date();
    const notificationDate = new Date(notificationCreated);

    const diffInMs = now.getTime() - notificationDate.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    } else {
      return this.timezoneService.formatDate(notificationDate, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  getNotificationAgeFromLocal(localTimeString: string): string {
    const now = new Date();
    const notificationDate = new Date(localTimeString); // Parse as local
    const diffInMs = now.getTime() - notificationDate.getTime();
    return this.getTimeAgoFromMs(diffInMs);
  }
  countUnreadNotifications(notifications: INotification[]): number {
    return notifications.filter((notification) => notification.read_at === null)
      .length;
  }
  setNotifications(notifications: INotification[]) {
    this._notifications.next(notifications);
  }

  getCurrentNotifications(): INotification[] {
    return this._notifications.value || [];
  }
}
